import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";

import { FiLogOut, FiMenu, FiMoon, FiSun, FiX } from "react-icons/fi";

import routes, { canUse, groupOrder } from "../routes";
import { RequestContext } from "../context/request.context";
import { UserContext } from "../context/user.context";
import { createAppTheme, tokens } from "../theme";

const SIDEBAR = 268;

/** Kirgan foydalanuvchi. Har render'da o'qiladi -- rol o'zgarsa darhol amal qiladi. */
const readUser = () => {
  try {
    return JSON.parse(window.localStorage.getItem("user"));
  } catch {
    return null;
  }
};

const NavItem = ({ route, active, onNavigate }) => (
  <Box
    component={Link}
    to={route.path}
    onClick={onNavigate}
    sx={{
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2.5,
      py: 1.15,
      textDecoration: "none",
      color: active ? "#FFFFFF" : "#9BA2C4",
      bgcolor: active ? tokens.brandSoft : "transparent",
      transition: "background-color .15s, color .15s",
      "&:hover": {
        bgcolor: active ? tokens.brandSoft : "rgba(255,255,255,.05)",
        color: "#FFFFFF",
      },
      "& svg": { fontSize: 18, flexShrink: 0 },
    }}
  >
    {/* Faol bo'limning chetidagi iliq chiziq -- yagona urg'u shu yerda */}
    {active && (
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 6,
          bottom: 6,
          width: 3,
          borderRadius: "0 3px 3px 0",
          bgcolor: tokens.spark,
          boxShadow: `0 0 12px ${tokens.spark}`,
        }}
      />
    )}
    {route.icon}
    <Typography
      variant="body2"
      sx={{
        fontWeight: active ? 600 : 500,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {route.name}
    </Typography>
  </Box>
);

const SidebarContent = ({ pathname, user, onNavigate, onLogout }) => {
  const grouped = useMemo(() => {
    const visible = routes.filter((r) => canUse(r, user));
    return groupOrder
      .map((g) => ({ group: g, items: visible.filter((r) => r.group === g) }))
      .filter((g) => g.items.length > 0);
  }, [user]);

  const initials = (user?.firstName || "?").slice(0, 1).toUpperCase();

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: tokens.ink }}>
      <Box sx={{ px: 2.5, py: 2.75, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          component="img"
          src="/assets/mark.png"
          alt=""
          sx={{ width: 30, height: 30, objectFit: "contain", flexShrink: 0 }}
        />
        <Box sx={{ lineHeight: 1 }}>
          <Typography
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 16,
              color: "#FFF",
              letterSpacing: "-0.01em",
            }}
          >
            Alight
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#6E76A0", letterSpacing: "0.1em", fontWeight: 600 }}>
            CRM
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", pb: 2 }}>
        {grouped.map(({ group, items }) => (
          <Box key={group} sx={{ mt: 1.5 }}>
            <Typography variant="overline" sx={{ px: 2.5, color: "#5C648F", display: "block", mb: 0.5 }}>
              {group}
            </Typography>
            {items.map((r) => (
              <NavItem key={r.path} route={r} active={pathname === r.path} onNavigate={onNavigate} />
            ))}
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderColor: tokens.inkLine }} />
      <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          sx={{ width: 32, height: 32, bgcolor: tokens.inkRaised, color: "#C9CEEA", fontSize: 14, fontWeight: 600 }}
        >
          {initials}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#E4E7F5" }} noWrap>
            {user ? `${user.firstName} ${user.lastName || ""}`.trim() : "—"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#6E76A0" }} noWrap>
            {user?.role || ""}
          </Typography>
        </Box>
        <Tooltip title="Chiqish">
          <IconButton size="small" onClick={onLogout} sx={{ color: "#8189B4" }}>
            <FiLogOut />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

function DashboardContent({ children }) {
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery("(min-width:900px)");
  const [mobileOpen, setMobileOpen] = useState(false);

  const [mode, setMode] = useState(() => window.localStorage.getItem("theme") || "light");
  useEffect(() => {
    window.localStorage.setItem("theme", mode);
  }, [mode]);
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const user = readUser();
  const { error, setError, success, setSuccess } = useContext(RequestContext);
  const { logout } = useContext(UserContext);

  // Kirmagan bo'lsa panel umuman ko'rsatilmaydi
  useEffect(() => {
    if (!window.localStorage.getItem("token")) window.location.href = "/login";
  }, []);

  const sidebar = (
    <SidebarContent pathname={pathname} user={user} onNavigate={() => setMobileOpen(false)} onLogout={logout} />
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
        {isDesktop ? (
          <Box
            component="nav"
            sx={{ width: SIDEBAR, flexShrink: 0, position: "fixed", inset: "0 auto 0 0", zIndex: 1200 }}
          >
            {sidebar}
          </Box>
        ) : (
          <Drawer
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            PaperProps={{ sx: { width: SIDEBAR, border: "none" } }}
          >
            {sidebar}
          </Drawer>
        )}

        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, ml: isDesktop ? `${SIDEBAR}px` : 0 }}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: { xs: 2, md: 4 },
              height: 60,
              bgcolor: "background.default",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            {!isDesktop && (
              <IconButton onClick={() => setMobileOpen(true)} edge="start">
                {mobileOpen ? <FiX /> : <FiMenu />}
              </IconButton>
            )}
            <Typography variant="h5" sx={{ flexGrow: 1 }} noWrap>
              {children.name}
            </Typography>
            <Tooltip title={mode === "dark" ? "Yorug' ko'rinish" : "Qorong'i ko'rinish"}>
              <IconButton size="small" onClick={() => setMode(mode === "dark" ? "light" : "dark")}>
                {mode === "dark" ? <FiSun /> : <FiMoon />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>{children.component}</Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default function Dashboard({ children }) {
  return <DashboardContent children={children} />;
}
